import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { scrollToId } from "@/components/Shared";

const LINKS = [
  { label: "Services", href: "#services", testId: "nav-link-services" },
  { label: "Sectors", href: "#sectors", testId: "nav-link-sectors" },
  { label: "Engagements", href: "#engagements", testId: "nav-link-engagements" },
  { label: "Leadership", href: "#leadership", testId: "nav-link-leadership" },
  { label: "Insights", href: "#insights", testId: "nav-link-insights" },
  { label: "Contact", href: "#contact", testId: "nav-link-contact" },
];

export const Logo = ({ className = "h-9 w-auto sm:h-10" }) => (
  <img
    src="/logo-gold.png"
    alt="BYOT Innovations | Strategic Consulting"
    data-testid="byot-logo"
    className={className}
  />
);

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, href) => {
    setOpen(false);
    if (!isHome) return;
    e.preventDefault();
    scrollToId(href);
  };

  const linkHref = (href) => (isHome ? href : `/${href}`);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 border-b transition-colors duration-500 ${
        scrolled || !isHome
          ? "border-[rgba(212,175,55,0.18)] bg-[#070E1B]/85 backdrop-blur-xl"
          : "border-transparent bg-[#070E1B]/40 backdrop-blur-md"
      }`}
    >
      <div className="mx-auto flex h-[76px] max-w-7xl items-center justify-between px-5 sm:px-8">
        <a
          href={isHome ? "#top" : "/"}
          data-testid="header-logo"
          onClick={(e) => go(e, "#top")}
          className="flex items-center"
        >
          <Logo />
        </a>

        <nav className="hidden items-center gap-6 lg:flex xl:gap-8" data-testid="desktop-nav">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={linkHref(l.href)}
              data-testid={l.testId}
              onClick={(e) => go(e, l.href)}
              className="font-mono-alt text-[10px] uppercase tracking-[0.2em] text-[#94A3B8] transition-colors duration-300 hover:text-[#D4AF37] xl:text-[11px] xl:tracking-[0.22em]"
            >
              {l.label}
            </a>
          ))}
          <a
            href={isHome ? "#contact" : "/#contact"}
            data-testid="header-cta-consultation"
            onClick={(e) => go(e, "#contact")}
            className="group flex items-center gap-2 border border-[#D4AF37]/60 px-5 py-2.5 font-mono-alt text-[11px] uppercase tracking-[0.22em] text-[#D4AF37] transition-colors duration-300 hover:bg-[#D4AF37] hover:text-[#070E1B]"
          >
            Consultation
            <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="text-[#D4AF37] lg:hidden"
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-nav"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-[rgba(212,175,55,0.15)] bg-[#070E1B]/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={linkHref(l.href)}
                  data-testid={`mobile-${l.testId}`}
                  onClick={(e) => go(e, l.href)}
                  className="border-b border-[rgba(212,175,55,0.1)] py-4 font-serif text-2xl text-[#F7F5F0] transition-colors hover:text-[#D4AF37]"
                >
                  {l.label}
                </a>
              ))}
              <a
                href={isHome ? "#contact" : "/#contact"}
                data-testid="mobile-cta-consultation"
                onClick={(e) => go(e, "#contact")}
                className="mt-5 border border-[#D4AF37] px-5 py-3.5 text-center font-mono-alt text-xs uppercase tracking-[0.25em] text-[#D4AF37]"
              >
                Request Consultation
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
